import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from 'firebase/auth';
import { firstValueFrom } from 'rxjs';
import { FederatedProviderId, IdentityService, identityErrorMessage } from '../../core/identity.service';
import { UserService } from '../../core/user.service';

type LoginStep = 'credentials' | 'company' | 'recovery';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const PROVIDER_RATE_LIMIT_COOLDOWN_MS = 30_000;
const PASSWORD_RESET_COOLDOWN_MS = 30_000;
export const PASSWORD_RESET_CONFIRMATION =
  'Si existe una cuenta que permita recuperar su contraseña con ese correo, recibirás las instrucciones para restablecerla.';

/**
 * Login de dcw-residentes con el diseño y las reglas del login de Diazar Cloud
 * (diazar-cloud-web, features/auth/login-page.tsx). La empresa se escribe,
 * como en el login anterior de dcw, y se recuerda la última usada.
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild('heading') heading?: ElementRef<HTMLHeadingElement>;

  step: LoginStep = 'credentials';
  initializing = true;
  busy = false;
  providerInProgress: FederatedProviderId | null = null;
  errorMessage: string | null = null;
  invalidFields: string[] = [];
  retryBlocked = false;

  email = '';
  password = '';
  showPassword = false;
  companyName = '';
  lastCompany = '';
  signedInEmail = '';

  resetEmail = '';
  resetSent = false;
  resetCooldownSeconds = 0;

  readonly resetConfirmation = PASSWORD_RESET_CONFIRMATION;
  readonly configured: boolean;
  readonly providers: Array<{ id: FederatedProviderId; label: string }>;

  private signedInUser: User | null = null;
  private timers: number[] = [];

  constructor(
    private identityService: IdentityService,
    private userService: UserService,
    private router: Router,
  ) {
    this.configured = identityService.isConfigured;
    this.providers = this.configured ? identityService.enabledProviders() : [];
    this.lastCompany = localStorage.getItem('companyName') ?? '';
    this.companyName = this.lastCompany;
  }

  async ngOnInit(): Promise<void> {
    if (!this.configured) {
      this.initializing = false;
      this.errorMessage = 'La configuración de acceso no está disponible para este ambiente.';
      return;
    }
    // Una sesión Firebase vigente (por ejemplo, al vencer el token de dcw) pasa directo a la empresa.
    const user = await this.identityService.currentUser().catch(() => null);
    this.initializing = false;
    if (user) this.goToCompanyStep(user);
  }

  ngOnDestroy(): void {
    this.timers.forEach((timer) => window.clearTimeout(timer));
  }

  get title(): string {
    if (this.step === 'recovery') return 'Recupera tu contraseña';
    if (this.step === 'company') return 'Elige una empresa';
    return 'Inicia sesión en Diazar Cloud';
  }

  get description(): string {
    if (this.step === 'recovery') return 'Ingresa tu correo para solicitar un enlace de recuperación.';
    if (this.step === 'company') return 'Escribe la empresa con la que quieres trabajar.';
    return 'Inicia sesión para continuar';
  }

  get credentialsDisabled(): boolean {
    return this.initializing || this.busy || this.providerInProgress !== null || this.retryBlocked || !this.configured;
  }

  isInvalid(field: string): boolean {
    return this.invalidFields.includes(field);
  }

  clearFeedback(): void {
    this.errorMessage = null;
    this.invalidFields = [];
  }

  async submitLogin(): Promise<void> {
    if (this.credentialsDisabled) return;

    const email = this.email.trim();
    const missing = [!email ? 'email' : null, !this.password ? 'password' : null].filter((f): f is string => !!f);
    if (missing.length > 0) {
      this.errorMessage = 'Completa correo y contraseña.';
      this.invalidFields = missing;
      return;
    }
    if (!EMAIL_PATTERN.test(email)) {
      this.errorMessage = 'Ingresa un correo válido.';
      this.invalidFields = ['email'];
      return;
    }
    if (email.length > 254 || this.password.length > 1024) {
      this.errorMessage = 'El correo admite máximo 254 caracteres y la contraseña, 1024.';
      this.invalidFields = [email.length > 254 ? 'email' : 'password'];
      return;
    }

    this.clearFeedback();
    this.busy = true;
    try {
      const user = await this.identityService.signInWithEmail(email, this.password);
      this.goToCompanyStep(user);
    } catch (error) {
      this.handleSignInError(error);
    } finally {
      this.password = '';
      this.showPassword = false;
      this.busy = false;
    }
  }

  async submitProvider(id: FederatedProviderId): Promise<void> {
    if (this.credentialsDisabled) return;
    this.clearFeedback();
    this.password = '';
    this.showPassword = false;
    this.providerInProgress = id;
    try {
      const user = await this.identityService.signInWithProvider(id);
      this.goToCompanyStep(user);
    } catch (error) {
      this.handleSignInError(error);
    } finally {
      this.providerInProgress = null;
    }
  }

  async submitCompany(): Promise<void> {
    if (this.busy || !this.signedInUser) return;
    const company = this.companyName.trim();
    if (!company) {
      this.errorMessage = 'Escribe el nombre de la empresa para continuar.';
      this.invalidFields = ['company'];
      return;
    }

    if (!this.signedInUser.emailVerified) {
      this.errorMessage = 'Tu correo todavía no está verificado. Revisa tu bandeja o usa otra cuenta.';
      return;
    }

    this.clearFeedback();
    this.busy = true;
    try {
      const profile = this.identityService.profileOf(this.signedInUser);
      const result = await firstValueFrom(this.userService.establishSession(profile, company));
      if (result.token) {
        await this.router.navigate(['/dashboard']);
        return;
      }
      this.errorMessage = result.errorMessage ?? 'No fue posible iniciar la sesión en esta empresa.';
      this.invalidFields = ['company'];
    } finally {
      this.busy = false;
    }
  }

  async useAnotherAccount(): Promise<void> {
    this.signedInUser = null;
    this.signedInEmail = '';
    this.email = '';
    this.password = '';
    this.clearFeedback();
    this.step = 'credentials';
    await this.identityService.signOut().catch(() => undefined);
    this.focusHeading();
  }

  openRecovery(): void {
    this.password = '';
    this.showPassword = false;
    this.clearFeedback();
    this.resetEmail = this.email.trim();
    this.resetSent = false;
    this.step = 'recovery';
    this.focusHeading();
  }

  closeRecovery(): void {
    this.clearFeedback();
    this.step = 'credentials';
    this.focusHeading();
  }

  async submitRecovery(): Promise<void> {
    if (this.busy || this.resetCooldownSeconds > 0) return;
    const email = this.resetEmail.trim();
    if (!EMAIL_PATTERN.test(email) || email.length > 254) {
      this.errorMessage = 'Ingresa un correo válido de máximo 254 caracteres.';
      this.invalidFields = ['reset-email'];
      return;
    }
    this.clearFeedback();
    this.busy = true;
    this.resetSent = false;
    try {
      await this.identityService.sendPasswordReset(email);
      this.resetSent = true;
    } catch (error) {
      // No se revela si la cuenta existe; sólo se informan fallas de servicio o de límite.
      const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code: unknown }).code) : '';
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        this.resetSent = true;
      } else {
        this.errorMessage = code === 'auth/too-many-requests'
          ? identityErrorMessage(error)
          : 'No fue posible solicitar la recuperación. Intenta nuevamente más tarde.';
      }
    } finally {
      this.busy = false;
      this.startResetCooldown();
    }
  }

  private goToCompanyStep(user: User): void {
    this.signedInUser = user;
    this.signedInEmail = user.email ?? '';
    this.companyName = this.lastCompany;
    this.clearFeedback();
    this.step = 'company';
    this.focusHeading();
  }

  private handleSignInError(error: unknown): void {
    this.errorMessage = identityErrorMessage(error);
    const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code: unknown }).code) : '';
    if (code === 'auth/too-many-requests') {
      this.retryBlocked = true;
      this.timers.push(window.setTimeout(() => (this.retryBlocked = false), PROVIDER_RATE_LIMIT_COOLDOWN_MS));
    }
  }

  private startResetCooldown(): void {
    this.resetCooldownSeconds = PASSWORD_RESET_COOLDOWN_MS / 1000;
    const tick = () => {
      this.resetCooldownSeconds -= 1;
      if (this.resetCooldownSeconds > 0) this.timers.push(window.setTimeout(tick, 1000));
    };
    this.timers.push(window.setTimeout(tick, 1000));
  }

  private focusHeading(): void {
    this.timers.push(window.setTimeout(() => this.heading?.nativeElement.focus()));
  }
}
